import React, { useState, useEffect, useContext } from "react";
import { 
  Box, Typography, Button, TextField, Dialog, DialogActions, 
  DialogContent, DialogTitle, IconButton, Grid, 
  Divider, Tooltip, Avatar
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import { ThemeProvider } from "@mui/material/styles";
import darkTheme from "../../style/theme";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { Icon as OpusIcon, Toggle as OpusToggle, Button as OpusButton } from "../../components/common/opus";
import AccessLockModal from "../../components/security/AccessLockModal";
import ViewDetailsModal from "../../components/ViewDetailModal.jsx";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import { AuthContext } from "../../contexts/AuthContext";
import { useAlert } from "../../contexts/AlertContext.js";
import ActivityTrackingModal from "../../components/common/ActivityTrackingModal";
import TimelineIcon from "@mui/icons-material/Timeline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { styled } from "@mui/material/styles";
import apiService from "../../services/api.service";
import StatusChip from "../../components/common/StatusChip";

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const Organization = () => {
  const { userInfo } = useContext(AuthContext);
  const userRole = userInfo?.data?.role?.name;
  const isSuperAdmin = userRole === "super_admin";
  const [lockModal, setLockModal] = useState({ open: false, id: null, name: "" });
  const alert = useAlert();
  
  // Main state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Logo state
  const [newLogo, setNewLogo] = useState(null);
  const [newLogoPreview, setNewLogoPreview] = useState(null);
  const [editLogo, setEditLogo] = useState(null);
  const [editLogoPreview, setEditLogoPreview] = useState(null);
  
  // Dialog state
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  
  // Selected data state
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedOrgForActivity, setSelectedOrgForActivity] = useState(null);
  
  // Form state
  const [newOrg, setNewOrg] = useState({
    name: "",
    cognito_group_id: "",
    logoUrl: "",
    account: {
      limit: {
        User: 0,
        Renderer: 0,
      },
      date: {
        endDate: "",
      },
    },
  });

  const [editOrgData, setEditOrgData] = useState({
    name: "",
    id: "",
    logoUrl: "",
    account: {
      limit: {
        User: 0,
        Renderer: 0,
      },
      date: {
        endDate: "",
      },
    },
  });

  const [newOrgErrors, setNewOrgErrors] = useState({});
  const [editOrgErrors, setEditOrgErrors] = useState({});
  // End Date as loaded when the Edit dialog was opened, so an untouched legacy past date
  // doesn't block saving unrelated field changes.
  const [editOriginalEndDate, setEditOriginalEndDate] = useState("");

  // Minimum selectable value for the datetime-local End Date inputs (now, local time)
  const getMinDateTimeLocal = () => {
    const now = new Date();
    now.setSeconds(0, 0);
    const tzOffsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 16);
  };

  // <input type="datetime-local"> only renders values shaped exactly YYYY-MM-DDTHH:mm;
  // stored dates come back as full ISO strings (seconds/ms/Z), which the input silently blanks.
  const toDateTimeLocalValue = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const tzOffsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffsetMs).toISOString().slice(0, 16);
  };

  const validateOrgForm = (org, { originalEndDate } = {}) => {
    const errors = {};

    if (!org.name || !org.name.trim()) {
      errors.name = "Organisation Name is required";
    }

    const userLimit = Number(org.account.limit.User);
    if (org.account.limit.User === "" || Number.isNaN(userLimit)) {
      errors.userLimit = "User Limit is required";
    } else if (userLimit < 0) {
      errors.userLimit = "User Limit cannot be negative";
    }

    const rendererLimit = Number(org.account.limit.Renderer);
    if (org.account.limit.Renderer === "" || Number.isNaN(rendererLimit)) {
      errors.rendererLimit = "Renderer Limit is required";
    } else if (rendererLimit < 0) {
      errors.rendererLimit = "Renderer Limit cannot be negative";
    }

    const endDateChanged = org.account.date.endDate !== originalEndDate;
    if (org.account.date.endDate && endDateChanged) {
      const endDate = new Date(org.account.date.endDate);
      if (endDate.getTime() < Date.now()) {
        errors.endDate = "End Date cannot be in the past";
      }
    }

    return errors;
  };

  const fetchOrganizations = async () => {
    try {
      const data = await apiService.getOrganizations();
      const formattedUsers = data.map((org, i) => ({
        id: org._id || i,
        orgId: org._id,
        name: org.name,
        status: org.status,
        logoUrl: org.logoUrl,
        account: org.account, 
        userLimit: org.account?.limit?.User || 0,
        rendererLimit: org.account?.limit?.Renderer || 0,
        endDate: org.account?.date?.endDate || "",
      }));

      setUsers(formattedUsers);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Logo upload handlers
  const handleNewLogoChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setNewLogo(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setNewLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleEditLogoChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setEditLogo(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload logo to S3
const uploadLogo = async (file, orgId = null, orgName = "") => {
  if (!file) return null;
  
  try {
    const response = await apiService.uploadOrganizationLogo(file, orgId, orgName);
    
    if (!response.success) {
      throw new Error('Logo upload failed');
    }
    
    return response.data.filePath; 
  } catch (error) {
    console.error('Error uploading logo:', error);
    alert.error('Failed to upload logo. Please try again.');
    return null;
  }
};

  // Maps a form field's `name` attribute to the key used in the errors state
  const ORG_FIELD_ERROR_KEY = {
    "name": "name",
    "account.limit.User": "userLimit",
    "account.limit.Renderer": "rendererLimit",
    "account.date.endDate": "endDate",
  };

  // Form change handlers
  const handleNewOrgChange = (event) => {
    const { name, value } = event.target;
    const keys = name.split(".");
    if (keys.length === 1) {
      setNewOrg((prev) => ({ ...prev, [name]: value }));
    } else {
      setNewOrg((prev) => {
        const updatedOrg = { ...prev };
        let current = updatedOrg;
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return updatedOrg;
      });
    }
    const errorKey = ORG_FIELD_ERROR_KEY[name];
    if (errorKey) {
      setNewOrgErrors((prev) => {
        if (!prev[errorKey]) return prev;
        const { [errorKey]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleEditOrgChange = (event) => {
    const { name, value } = event.target;
    const keys = name.split(".");
    if (keys.length === 1) {
      setEditOrgData((prev) => ({ ...prev, [name]: value }));
    } else {
      setEditOrgData((prev) => {
        const updatedOrg = { ...prev };
        let current = updatedOrg;
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return updatedOrg;
      });
    }
    const errorKey = ORG_FIELD_ERROR_KEY[name];
    if (errorKey) {
      setEditOrgErrors((prev) => {
        if (!prev[errorKey]) return prev;
        const { [errorKey]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  // Dialog handlers
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setNewLogo(null);
    setNewLogoPreview(null);
    setNewOrgErrors({});
  };
  
  const handleDetailsOpen = (org) => {
    setSelectedOrg(org);
    setDetailsOpen(true);
  };
  
  const handleDetailsClose = () => setDetailsOpen(false);
  
  const handleEditOrg = (org) => {
    const endDate = toDateTimeLocalValue(org.endDate);
    setEditOrgData({
      id: org.orgId,
      name: org.name,
      logoUrl: org.logoUrl || "",
      account: {
        limit: {
          User: org.userLimit || 0,
          Renderer: org.rendererLimit || 0,
        },
        date: {
          endDate,
        },
      },
    });
    setEditOriginalEndDate(endDate);
    setEditLogoPreview(org.logoUrl || null);
    setEditOrgErrors({});
    setEditDialogOpen(true);
  };
  
  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditLogo(null);
    setEditLogoPreview(null);
    setEditOrgErrors({});
    setEditOriginalEndDate("");
    setEditOrgData({
      name: "",
      id: "",
      logoUrl: "",
      account: {
        limit: {
          User: 0,
          Renderer: 0,
        },
        date: {
          endDate: "",
        },
      },
    });
  };

  // Activity modal handlers
  const handleOpenActivityModal = (orgId, orgName) => {
    setSelectedOrgForActivity({ id: orgId, name: orgName });
    setActivityModalOpen(true);
  };

  const handleCloseActivityModal = () => {
    setActivityModalOpen(false);
    setSelectedOrgForActivity(null);
  };

  const handleSaveNewOrg = async () => {
    const errors = validateOrgForm(newOrg);
    setNewOrgErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setSaving(true);
    let logoUrl = null;

    try {
      const initialOrg = {
        ...newOrg,
        logoUrl: "",
        account: {
          ...newOrg.account,
        },
      };
    
      const createResponse = await apiService.createOrganization(initialOrg);
      
      if (!createResponse.organization) {
        throw new Error('Failed to create organization');
      }

      const createdOrg = createResponse.organization;
      
      if (newLogo && createdOrg._id) {
        logoUrl = await uploadLogo(newLogo, createdOrg._id, newOrg.name);
        
        if (logoUrl) {
          const updateResponse = await apiService.updateOrganizationLogo(createdOrg._id, logoUrl);

          // /common PUT returns the raw doc (no success envelope); only a null/error response is a real failure.
          if (!updateResponse || updateResponse.error) {
            console.warn("Logo URL update failed, but organization was created");
          }
        }
      }
      
      setNewOrg({
        name: "",
        cognito_group_id: "",
        logoUrl: "",
        account: {
          limit: {
            User: 0,
            Renderer: 0,
          },
          date: {
            endDate: "",
          },
        },
      });
      
      alert.success("Organization created successfully");
      handleClose();
      fetchOrganizations();
    } catch (error) {
      console.error("Error adding organization:", error);
      alert.error('Failed to create organization. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdateOrg = async () => {
    const errors = validateOrgForm(editOrgData, { originalEndDate: editOriginalEndDate });
    setEditOrgErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setSaving(true);
    let logoUrl = editOrgData.logoUrl;
    console.log("🚀 ~ handleUpdateOrg ~ logoUrl:", editOrgData)
    
    try {
      // Upload new logo if present
      if (editLogo) {
        const uploadedLogoUrl = await uploadLogo(editLogo, editOrgData.id, editOrgData.name);
        if (uploadedLogoUrl) {
          logoUrl = uploadedLogoUrl;
        }
      }
      
      const formattedOrg = {
        name: editOrgData.name,
        logoUrl: logoUrl,
        account: {
          ...editOrgData.account,
        },
      };

      const response = await apiService.updateOrganization(editOrgData.id, formattedOrg);

      // The /common/:collection/:id PUT route returns the raw updated document (no {success}
      // envelope). request() already throws on any non-2xx, and findByIdAndUpdate returns null
      // when the org isn't found, so a null/error response is the only real failure here.
      if (!response || response.error) {
        throw new Error(response?.error || "Failed to update organization");
      }

      alert.success("Organization updated successfully");
      handleEditClose();
      fetchOrganizations();
    } catch (error) {
      console.error("Error updating organization:", error);
      alert.error("Failed to update organization. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOrganizationStatus = async (orgId, currentStatus) => {
    try {
      setLoading(true);
      const response = await apiService.toggleOrganizationStatus(orgId, currentStatus);

      // The /organization/:id/status route returns { status, message, organization } (no `success`).
      // request() throws on non-2xx, so a resolved response means the toggle succeeded.
      if (response?.error) {
        alert.error(response.message || response.error || `Failed to update organization status`);
        return;
      }

      alert.success(response.message || `Organization status changed to ${currentStatus}`);
      fetchOrganizations();
    } catch (error) {
      alert.error(error.message || "Failed to update organization status");
    } finally {
      setLoading(false);
    }
  };

  // DataGrid columns
  const columns = [
    { 
      field: "logoUrl", 
      headerName: "Logo",
      width: 70,
      renderCell: (params) => (
        <Avatar
          src={params.value}
          alt={params.row.name}
          sx={{ width: 40, height: 40 }}
        >
          {params.row.name.charAt(0).toUpperCase()}
        </Avatar>
      )
    },
    { field: "name", headerName: "Organisation Name", flex: 1, renderCell: (params) => <span style={{ fontWeight: 650, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.row.name}</span> },
    { field: "userLimit", headerName: "User Limit", flex: 0.7, renderCell: (params) => params.row.userLimit ? <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>{params.row.userLimit}</span> : <span style={{ fontSize: 12.5, color: "var(--text-dim)", fontStyle: "italic" }}>No limit</span> },
    { field: "rendererLimit", headerName: "Renderer Limit", flex: 0.7, renderCell: (params) => params.row.rendererLimit ? <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>{params.row.rendererLimit}</span> : <span style={{ fontSize: 12.5, color: "var(--text-dim)", fontStyle: "italic" }}>No limit</span> },
    { field: "status", headerName: "Status", flex: 0.7, renderCell: (params) => <StatusChip status={params.row.status} /> },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex" }}>
          <Tooltip title="View Details">
            <IconButton onClick={() => handleDetailsOpen(params.row)} color="info">
              <OpusIcon name="eye" size={18} />
            </IconButton>
          </Tooltip>
          {isSuperAdmin && (
            <Tooltip title="Access lock (region / IP)">
              <IconButton onClick={() => setLockModal({ open: true, id: params.row.orgId, name: params.row.name })} sx={{ color: "var(--text-dim)", "&:hover": { color: "var(--amber)" } }}>
                <OpusIcon name="lock" size={18} />
              </IconButton>
            </Tooltip>
          )}
          {editMode && isSuperAdmin && (
            <>
              <Tooltip title="View Activity Timeline">
                <IconButton
                  onClick={() => handleOpenActivityModal(params.row.orgId, params.row.name)}
                  color="info"
                >
                  <OpusIcon name="activity" size={18} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Edit">
                <IconButton onClick={() => handleEditOrg(params.row)} color="info">
                  <OpusIcon name="edit" size={18} />
                </IconButton>
              </Tooltip>
              <OpusToggle
                checked={params.row.status === 'active'}
                onChange={() => handleToggleOrganizationStatus(params.row.orgId, params.row.status)}
              />
            </>
          )}
        </Box>
      ),
    },
  ];

  // Fields for details modal
  const fields = [
    { key: "name", label: "Organisation Name" },
    { key: "logoUrl", label: "Logo", type: "image" },
    { key: "userLimit", label: "User Limit" },
    { key: "rendererLimit", label: "Renderer Limit" },
    { key: "endDate", label: "End Date" },
    { key: "status", label: "Status" },
  ];

  return (
    <ThemeProvider theme={darkTheme}>
      <Grid
        container
        spacing={1}
        sx={{height:'100vh'}}
      >
        <Grid size={12} >
          <DashboardBreadcrumbs rightAction={isSuperAdmin &&
            <OpusButton variant="primary" icon="plus" onClick={handleClickOpen}>Add Organisation</OpusButton>
          }/>
        </Grid>

        <Grid size={12} sx={{height:'100%'}}>
          <Box sx={{
              height: "calc(100% - 67px)",
              width: "100%",
              p:2,
              background: "var(--surface-1)",
              border: "1px solid var(--border)",
              borderRadius: "var(--r-lg)",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
          }}>
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography sx={{ fontSize: 19, fontWeight: 500, color: "#fff" }}>Overview</Typography>
              {isSuperAdmin && (
                <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, color: "var(--text-muted)", fontWeight: 600, cursor: "pointer" }}>
                  Edit Mode <OpusToggle checked={editMode} onChange={(v) => setEditMode(v)} />
                </Box>
              )}
            </Box>
            
            <DataGrid
              rows={users}
              columns={columns}
              loading={loading}
              checkboxSelection
              disableRowSelectionOnClick
              rowHeight={52}
              columnHeaderHeight={44}
              disableColumnMenu
              sx={{
                mt: 2,
                flex: 1,
                minHeight: 0,
                border: 'none',
                // OPUS-913: dividers/cell padding inherit from the global MuiDataGrid theme.
                '& .MuiDataGrid-footerContainer': { borderTop: 'none' },
              }}
            />

            {/* Add Organization Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
              <DialogTitle>Add New Organisation</DialogTitle>
              <DialogContent>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  <Box
                    sx={{ 
                      position: 'relative',
                      width: 100,
                      height: 100,
                      mb: 2,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      '&:hover .logo-upload-overlay': {
                        opacity: 1
                      }
                    }}
                  >
                    <Avatar
                      src={newLogoPreview}
                      alt="Organization Logo Preview"
                      sx={{ width: '100%', height: '100%' }}
                    >
                      {newOrg.name ? newOrg.name.charAt(0).toUpperCase() : "O"}
                    </Avatar>
                    <Box 
                      className="logo-upload-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '50%',
                        opacity: 0,
                        transition: 'opacity 0.3s',
                        cursor: 'pointer'
                      }}
                      component="label"
                    >
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={handleNewLogoChange}
                        style={{ display: 'none' }}
                      />
                      <CameraAltIcon sx={{ color: 'white', fontSize: 30 }} />
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Hover and click to upload logo
                  </Typography>
                </Box>
                
                <TextField
                  autoFocus
                  margin="dense"
                  name="name"
                  label="Organisation Name"
                  type="text"
                  fullWidth
                  value={newOrg.name}
                  onChange={handleNewOrgChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!newOrgErrors.name}
                  helperText={newOrgErrors.name}
                />

                <TextField
                  margin="dense"
                  name="account.limit.User"
                  label="User Limit"
                  type="number"
                  fullWidth
                  helperText={newOrgErrors.userLimit || "0 = unlimited"}
                  value={newOrg.account.limit.User}
                  onChange={handleNewOrgChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0 }}
                  error={!!newOrgErrors.userLimit}
                />
                <TextField
                  margin="dense"
                  name="account.limit.Renderer"
                  label="Renderer Limit"
                  type="number"
                  fullWidth
                  value={newOrg.account.limit.Renderer}
                  onChange={handleNewOrgChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0 }}
                  error={!!newOrgErrors.rendererLimit}
                  helperText={newOrgErrors.rendererLimit}
                />
                <TextField
                  margin="dense"
                  name="account.date.endDate"
                  label="End Date"
                  type="datetime-local"
                  fullWidth
                  value={newOrg.account.date.endDate}
                  onChange={handleNewOrgChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: getMinDateTimeLocal() }}
                  error={!!newOrgErrors.endDate}
                  helperText={newOrgErrors.endDate}
                />
              </DialogContent>
              <DialogActions>
                <Button 
                  onClick={handleClose} 
                 variant="outlined" color="info"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveNewOrg} 
                  variant="contained" color="seafoamGreen85"
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} /> : "Save"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Edit Organization Dialog */}
            <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
              <DialogTitle>Edit Organisation</DialogTitle>
              <DialogContent>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  <Box
                    sx={{ 
                      position: 'relative',
                      width: 100,
                      height: 100,
                      mb: 2,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      '&:hover .logo-upload-overlay': {
                        opacity: 1
                      }
                    }}
                  >
                    <Avatar
                      src={editLogoPreview}
                      alt="Organization Logo Preview"
                      sx={{ width: '100%', height: '100%' }}
                    >
                      {editOrgData.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box 
                      className="logo-upload-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '50%',
                        opacity: 0,
                        transition: 'opacity 0.3s',
                        cursor: 'pointer'
                      }}
                      component="label"
                    >
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={handleEditLogoChange}
                        style={{ display: 'none' }}
                      />
                      <CameraAltIcon sx={{ color: 'white', fontSize: 30 }} />
                    </Box>
                  </Box>
                 
                </Box>
                
                <TextField
                  autoFocus
                  margin="dense"
                  name="name"
                  label="Organization Name"
                  type="text"
                  fullWidth
                  value={editOrgData.name}
                  onChange={handleEditOrgChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!editOrgErrors.name}
                  helperText={editOrgErrors.name}
                />

                <TextField
                  margin="dense"
                  name="account.limit.User"
                  label="User Limit"
                  type="number"
                  fullWidth
                  helperText={editOrgErrors.userLimit || "0 = unlimited"}
                  value={editOrgData.account.limit.User}
                  onChange={handleEditOrgChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0 }}
                  error={!!editOrgErrors.userLimit}
                />
                <TextField
                  margin="dense"
                  name="account.limit.Renderer"
                  label="Renderer Limit"
                  type="number"
                  fullWidth
                  value={editOrgData.account.limit.Renderer}
                  onChange={handleEditOrgChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0 }}
                  error={!!editOrgErrors.rendererLimit}
                  helperText={editOrgErrors.rendererLimit}
                />
                <TextField
                  margin="dense"
                  name="account.date.endDate"
                  label="End Date"
                  type="datetime-local"
                  fullWidth
                  value={editOrgData.account.date.endDate}
                  onChange={handleEditOrgChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: getMinDateTimeLocal() }}
                  error={!!editOrgErrors.endDate}
                  helperText={editOrgErrors.endDate}
                />
              </DialogContent>
              <DialogActions>
                <Button 
                  onClick={handleEditClose} 
                  sx={{ 
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' } 
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdateOrg} 
                  variant="contained" 
                  color="primary"
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} /> : "Update"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* View Details Modal */}
            <ViewDetailsModal
              open={detailsOpen}
              onClose={handleDetailsClose}
              data={selectedOrg}
              fields={fields}
              title="Organisation Details"
              theme={darkTheme}
            />

            {/* View Details Modal */}
            <ViewDetailsModal
              open={detailsOpen}
              onClose={handleDetailsClose}
              data={selectedOrg}
              fields={fields}
              title="Organisation Details"
              theme={darkTheme}
            />
          </Box>
        </Grid>
      </Grid>

      {selectedOrgForActivity && (
        <ActivityTrackingModal
          open={activityModalOpen}
          onClose={handleCloseActivityModal}
          orgId={selectedOrgForActivity.id}
          title={`Activity History: ${selectedOrgForActivity.name}`}
          showOrgActivities={true}
          showUserSelector={false}
          defaultViewType="organization"
        />
      )}
      <AccessLockModal
        open={lockModal.open}
        onClose={() => setLockModal({ open: false, id: null, name: "" })}
        type="org"
        id={lockModal.id}
        name={lockModal.name}
      />
    </ThemeProvider>
  );
};




export default Organization;
